import { useEffect, useRef } from "react";

import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { useAuth } from "@hl/shared-features/lib/features/auth";
import { SignInButton } from "@hl/shared-features/lib/features/auth/SignInButton";
import {
  TransactionStateType,
  useTransactionState,
} from "@hl/shared-features/lib/features/evm-tx/TransactionContext";
import { getCurrencySymbol } from "@hl/shared-features/lib/utils/currency";
import { Box, Button, Text } from "@mantine/core";
import { formatEther, parseEther } from "viem";

import useMintState, { MintStateRequired } from "../../../hooks/useMintState";
import ErrorBox from "../MintVector/ErrorBox";
import { useTxButtonState } from "../MintVector/tx-state";

import { useRankedAuction } from "./hooks";

export const ClaimCard = () => {
  const { rankedAuction, mintVector } = useRankedAuction();
  const ref = useRef(null);

  const { authenticated } = useAuth();
  if (!rankedAuction) {
    return null;
  }
  const { userBids, userNumTokensLeftToClaim: numToClaim } = rankedAuction;
  const wonBids = userBids?.filter((x) => x.currentlyValid) ?? [];
  const hasWonBids = wonBids.length !== 0;
  const totalAmount = formatEther(
    wonBids.reduce((total, bid) => total + parseEther(bid.bidAmount), 0n)
  );
  const currencySymbol = getCurrencySymbol(
    mintVector.chainId!,
    mintVector.paymentCurrency?.symbol
  );

  const endDate = new Date(rankedAuction.end).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });

  return (
    <>
      {!hasWonBids && (
        <Box>
          <Text size={24} fw={WEIGHT_BOLD}>
            Auction ended
          </Text>
          <Text size="sm" color={TEXT_COLOR.SECONDARY} mt="md">
            This auction ended on {endDate}
          </Text>
        </Box>
      )}
      {!authenticated && <SignInButton ref={ref} size="xl" fullWidth mt={16} />}

      {hasWonBids && (
        <Box>
          <Text size={24} fw={WEIGHT_BOLD}>
            {wonBids.length === 1
              ? `Congrats! You won a token at ${totalAmount} ${currencySymbol}`
              : `Congrats! You won ${wonBids.length} tokens for a total of ${totalAmount} ${currencySymbol}`}
          </Text>
          <Text size="sm" color={TEXT_COLOR.SECONDARY} mt="md">
            This auction ended on {endDate}
          </Text>
          {(numToClaim ?? 0) !== 0 && <ClaimButton numToClaim={numToClaim!} />}
        </Box>
      )}
    </>
  );
};

const ClaimButton = ({ numToClaim }: { numToClaim: number }) => {
  const {
    handleMint,
    mintVector,
    loadingClaimMint,
    txnId,
    setNumTokensToMint,
  } = useMintState() as MintStateRequired;
  const { refetchRankedAuction } = useRankedAuction();

  const handleClick = () => {
    handleMint(mintVector.chainId!);
  };
  const transactionState = useTransactionState(txnId);
  const txType = transactionState?.type;

  useEffect(() => {
    if (numToClaim) {
      setNumTokensToMint(numToClaim);
    }
  }, [numToClaim]);

  useEffect(() => {
    if (!txType) {
      return;
    }
    if (txType === TransactionStateType.Done) {
      if (refetchRankedAuction) {
        refetchRankedAuction();
      }
    }
  }, [txType, refetchRankedAuction]);

  const { isMinting, errorMsg, txBtnLabel } = useTxButtonState(txnId);

  return (
    <>
      <Button
        size="xl"
        fullWidth
        mt="md"
        mb="sm"
        onClick={handleClick}
        loading={loadingClaimMint || isMinting}
      >
        {txBtnLabel ?? `Claim token${numToClaim > 1 ? "s" : ""}`}
      </Button>
      {errorMsg && <ErrorBox message={errorMsg} />}
    </>
  );
};
